import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Mail,
  Calendar,
  MessageSquare,
  TrendingUp,
  LogOut,
  Search,
  Download,
  Phone,
  Clock,
  Home
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Check if user is admin
    if (!isAuthenticated || user?.email !== 'admin@rcawards.com') {
      navigate('/admin/login');
      return;
    }
    
    fetchData();
  }, [isAuthenticated, user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch statistics
      const statsRes = await axios.get(`${API_URL}/api/admin/stats`, {
        withCredentials: true
      });
      setStats(statsRes.data);

      // Fetch contacts
      const contactsRes = await axios.get(`${API_URL}/api/admin/contacts?limit=100`, {
        withCredentials: true
      });
      setContacts(contactsRes.data.data);

      // Fetch bookings
      const bookingsRes = await axios.get(`${API_URL}/api/admin/bookings?limit=100`, {
        withCredentials: true
      });
      setBookings(bookingsRes.data.data);

      // Fetch newsletters
      const newslettersRes = await axios.get(`${API_URL}/api/admin/newsletters?limit=100`, {
        withCredentials: true
      });
      setNewsletters(newslettersRes.data.data);

      // Fetch users
      const usersRes = await axios.get(`${API_URL}/api/admin/users?limit=100`, {
        withCredentials: true
      });
      setUsers(usersRes.data.data);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        navigate('/admin/login');
      }
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredBookings = bookings.filter(booking =>
    booking.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.roomType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredNewsletters = newsletters.filter(newsletter =>
    newsletter.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    (u.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-[#1a365d] to-[#2d4a7c] text-white p-2 rounded-lg">
                <Users size={24} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs text-gray-500">RC Awards</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-accent transition-colors"
              >
                <Home size={18} />
                <span className="hidden sm:inline">Website</span>
              </button>
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {activeTab === 'overview' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <MessageSquare className="text-blue-600" size={24} />
                </div>
                <span className="text-xs text-green-600 font-medium">
                  +{stats.recent_contacts} this week
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{stats.total_contacts}</h3>
              <p className="text-sm text-gray-600">Contact Inquiries</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <Calendar className="text-orange-600" size={24} />
                </div>
                <span className="text-xs text-green-600 font-medium">
                  +{stats.recent_bookings} this week
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{stats.total_bookings}</h3>
              <p className="text-sm text-gray-600">Booking Requests</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <Mail className="text-green-600" size={24} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{stats.total_newsletters}</h3>
              <p className="text-sm text-gray-600">Newsletter Subscribers</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Users className="text-purple-600" size={24} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{stats.total_users}</h3>
              <p className="text-sm text-gray-600">Registered Users</p>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {['overview', 'contacts', 'bookings', 'newsletters', 'users'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm capitalize transition-colors ${
                    activeTab === tab
                      ? 'border-accent text-accent'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Search Bar */}
          {activeTab !== 'overview' && (
            <div className="p-6 border-b border-gray-200 flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                />
              </div>
              <button
                onClick={() => {
                  if (activeTab === 'contacts') exportToCSV(filteredContacts, 'contacts');
                  if (activeTab === 'bookings') exportToCSV(filteredBookings, 'bookings');
                  if (activeTab === 'newsletters') exportToCSV(filteredNewsletters, 'newsletters');
                  if (activeTab === 'users') exportToCSV(filteredUsers, 'users');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
              >
                <Download size={18} />
                Export CSV
              </button>
            </div>
          )}

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="text-center py-12">
                <TrendingUp className="mx-auto text-gray-400 mb-4" size={64} />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome to Admin Dashboard</h3>
                <p className="text-gray-600 mb-6">Select a tab to view detailed lead information</p>
              </div>
            )}

            {activeTab === 'contacts' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Phone</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Type</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map((contact) => (
                      <tr key={contact.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Clock size={14} />
                            {new Date(contact.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{contact.name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <a href={`mailto:${contact.email}`} className="text-accent hover:underline">
                            {contact.email}
                          </a>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {contact.phone ? (
                            <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-accent hover:underline">
                              <Phone size={14} />
                              {contact.phone}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full capitalize">
                            {contact.inquiryType}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 max-w-xs truncate">{contact.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredContacts.length === 0 && (
                  <div className="text-center py-12 text-gray-500">No contacts found</div>
                )}
              </div>
            )}

            {activeTab === 'bookings' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Phone</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Check-in</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Check-out</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Guests</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Room Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Clock size={14} />
                            {new Date(booking.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{booking.name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <a href={`mailto:${booking.email}`} className="text-accent hover:underline">
                            {booking.email}
                          </a>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {booking.phone ? (
                            <a href={`tel:${booking.phone}`} className="flex items-center gap-1 text-accent hover:underline">
                              <Phone size={14} />
                              {booking.phone}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{booking.checkIn}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{booking.checkOut}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{booking.guests}</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                            {booking.roomType}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredBookings.length === 0 && (
                  <div className="text-center py-12 text-gray-500">No bookings found</div>
                )}
              </div>
            )}

            {activeTab === 'newsletters' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Date Subscribed</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNewsletters.map((newsletter) => (
                      <tr key={newsletter.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Clock size={14} />
                            {new Date(newsletter.subscribed_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <a href={`mailto:${newsletter.email}`} className="text-accent hover:underline">
                            {newsletter.email}
                          </a>
                        </td>
                        <td className="py-3 px-4">
                          <a
                            href={`mailto:${newsletter.email}`}
                            className="text-sm text-accent hover:underline"
                          >
                            Send Email
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredNewsletters.length === 0 && (
                  <div className="text-center py-12 text-gray-500">No subscribers found</div>
                )}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Joined</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Auth Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Clock size={14} />
                            {new Date(u.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-[#0F4C3A]/10 flex items-center justify-center">
                                <span className="text-[10px] font-bold text-[#0F4C3A]">{(u.name || '?')[0].toUpperCase()}</span>
                              </div>
                            )}
                            {u.name || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <a href={`mailto:${u.email}`} className="text-accent hover:underline">
                            {u.email}
                          </a>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            u.google_id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {u.google_id ? 'Google' : 'Email'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && (
                  <div className="text-center py-12 text-gray-500">No users found</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
